import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useApiError } from '@/hooks/use-api-error';
import { ApiTimeoutError } from '@/lib/api-client';
import { IconBusy, IconError, IconWarning } from '@/lib/icons';
import { cn } from '@/lib/utils';

/**
 * Shared page-level primitives every list/detail screen uses (ui-contract.md
 * section 2: loading skeleton, empty/no-results states, and an error panel
 * with a retry action). Ported from the Unified Access web kit's
 * `features/portal/ui.tsx` and generalised for this app's namespaces.
 */
export function PageHeading({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {description && <p className="max-w-3xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Loading() {
  const { t } = useTranslation('common');
  return (
    <p role="status" className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
      <IconBusy className="size-4" aria-hidden />
      {t('state.loading')}
    </p>
  );
}

export function Forbidden() {
  const { t } = useTranslation('common');
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-md border p-10 text-center"
    >
      <IconError className="size-8 text-destructive" aria-hidden />
      <p className="text-base font-medium">{t('accessDenied.title')}</p>
      <p className="text-sm text-muted-foreground">{t('accessDenied.description')}</p>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

export function Failure({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useTranslation('common');
  const toMessage = useApiError();
  // Rule E8: a timeout means the outcome is unknown, not failed - visually
  // distinguished (caution tone), never the destructive one.
  const timedOut = error instanceof ApiTimeoutError;
  const failure = toMessage(error);
  return (
    <div
      role="alert"
      className={cn(
        'space-y-3 rounded-md border p-4',
        timedOut ? 'border-amber-500/40' : 'border-destructive/30',
      )}
    >
      <div className="flex items-start gap-2">
        {timedOut ? (
          <IconWarning className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
        ) : (
          <IconError className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
        )}
        <p className="text-sm">{failure.message}</p>
      </div>
      {retry && (
        <Button type="button" variant="outline" onClick={retry}>
          {t('actions.retry')}
        </Button>
      )}
    </div>
  );
}
